import { motion } from 'framer-motion';
import { Check, Image, RectangleHorizontal, Smartphone } from 'lucide-react';
import { type ReactNode, useId, useRef, useState } from 'react';
import { toast } from 'sonner';

import { MAX_PREVIEW_IMAGES, MIN_PREVIEW_IMAGES } from '../../constants/form';
import { cn } from '../../lib/utils';
import type { ImageDimensions } from '../../utils/imageDimensions';
import type { PreviewOrientation } from '../../utils/imageValidation';
import { validateMediaFile } from '../../utils/mediaUtils';
import * as Form from '../Form';
import { SNAPPY_SPRING, StatusPill } from '../portal';

const MEDIA_ACCEPT =
  'image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,.jpg,.jpeg,.png,.gif,.webp,.mp4';

interface PreviewUploadsFieldProps {
  dappPreviews: (File | null)[];
  existingPreviewUrls?: (string | null)[];
  previewDimensions?: ImageDimensions | null;
  orientation: PreviewOrientation;
  /** Locked once any preview exists — all previews share one orientation. */
  isOrientationLocked: boolean;
  /** Hide the toggle while saved previews are still being measured. */
  isResolvingOrientation?: boolean;
  onOrientationChange: (orientation: PreviewOrientation) => void;
  /** Empty-state drop zone: files fill the slots in order. */
  onFilesSelected: (files: File[]) => void | Promise<void>;
  renderUploadSlot: (index: number) => ReactNode;
}

const EMPTY_PREVIEW_URLS: (string | null)[] = [];

const ORIENTATIONS: {
  key: PreviewOrientation;
  label: string;
  ratio: string;
  icon: ReactNode;
}[] = [
  {
    key: 'portrait',
    label: 'Portrait',
    ratio: '9:16',
    icon: <Smartphone className="size-3" />,
  },
  {
    key: 'landscape',
    label: 'Landscape',
    ratio: '16:9',
    icon: <RectangleHorizontal className="size-3" />,
  },
];

export function getVisiblePreviewSlotCount(filledIndexes: number[]) {
  const lastFilled = filledIndexes.length ? Math.max(...filledIndexes) : -1;
  return Math.min(
    MAX_PREVIEW_IMAGES,
    Math.max(MIN_PREVIEW_IMAGES, filledIndexes.length + 1, lastFilled + 2),
  );
}

function pickValidMediaFiles(fileList: FileList | File[]): File[] {
  return Array.from(fileList).filter((file) => {
    const result = validateMediaFile(file);
    if (!result.valid && result.error) {
      toast.error(`${file.name}: ${result.error.message}`);
    }
    return result.valid;
  });
}

export function PreviewUploadsField({
  dappPreviews,
  existingPreviewUrls = EMPTY_PREVIEW_URLS,
  previewDimensions,
  orientation,
  isOrientationLocked,
  isResolvingOrientation = false,
  onOrientationChange,
  onFilesSelected,
  renderUploadSlot,
}: PreviewUploadsFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const thumbLayoutId = `${useId()}-orientation-thumb`;

  const slotCount = Math.max(dappPreviews.length, existingPreviewUrls.length);
  const filledIndexes = Array.from({ length: slotCount }, (_, i) => i).filter(
    (i) => Boolean(dappPreviews[i]) || Boolean(existingPreviewUrls[i]),
  );
  const filled = filledIndexes.length;
  const visibleSlots = getVisiblePreviewSlotCount(filledIndexes);
  const isLandscape = orientation === 'landscape';

  // Match the real ratio so uploads fill the slot without letterboxing.
  const slotAspectRatio = previewDimensions
    ? `${previewDimensions.width} / ${previewDimensions.height}`
    : isLandscape
      ? '16 / 9'
      : '9 / 16';

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList?.length) {
      return;
    }
    const files = pickValidMediaFiles(fileList);
    if (files.length) {
      void onFilesSelected(files);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <Form.FieldLabel
          label="dApp Preview"
          required
          explanation="Upload 4-8 screenshots of your dApp. JPG, PNG, GIF, webp, or mp4. 3MB max (30MB for video)."
        />

        <div
          className={cn(
            'flex flex-wrap items-center gap-3',
            isResolvingOrientation && 'invisible',
          )}
        >
          <div
            role="radiogroup"
            aria-label="Preview orientation"
            className="flex h-8 items-center rounded-lg border border-seeker-700 p-px"
          >
            {ORIENTATIONS.map((option) => {
              const isActive = option.key === orientation;
              return (
                <button
                  key={option.key}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  disabled={isOrientationLocked && !isActive}
                  onClick={() => onOrientationChange(option.key)}
                  className={cn(
                    'relative flex h-full items-center rounded-[7px] px-3 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70 disabled:cursor-not-allowed disabled:opacity-50',
                    isActive
                      ? 'text-black'
                      : 'text-white enabled:hover:bg-white/5',
                  )}
                >
                  {isActive ? (
                    <motion.span
                      layoutId={thumbLayoutId}
                      transition={SNAPPY_SPRING}
                      aria-hidden="true"
                      className="absolute inset-0 rounded-[7px] bg-white"
                    />
                  ) : null}
                  <span className="relative flex items-center gap-1.5">
                    {option.icon}
                    <span className="text-xs font-medium">{option.label}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <span className="text-sm text-seeker-400">
            {ORIENTATIONS.find((option) => option.key === orientation)?.ratio}{' '}
            aspect ratio
          </span>
        </div>
      </div>

      {filled === 0 ? (
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload dApp previews"
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={cn(
            'flex h-[257px] w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-seeker-700 transition-colors duration-150 hover:border-seeker-500 focus-visible:border-white focus-visible:outline-none',
            isDragOver && 'border-white bg-white/5',
          )}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={MEDIA_ACCEPT}
            aria-label="Choose dApp preview files"
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <Image className="size-8 text-white" aria-hidden="true" />
          <div className="flex flex-col items-center gap-1">
            <span className="text-sm font-bold text-white">
              Drag &amp; drop or browse files
            </span>
            <span className="text-xs leading-4 text-seeker-400">
              {MIN_PREVIEW_IMAGES} required
            </span>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            'grid gap-2',
            isLandscape
              ? 'grid-cols-1 sm:grid-cols-2'
              : 'grid-cols-2 sm:grid-cols-4',
          )}
        >
          {Array.from({ length: visibleSlots }, (_, index) => {
            const isFilled = filledIndexes.includes(index);
            const isRequiredSlot = index < MIN_PREVIEW_IMAGES;
            return (
              <div
                key={index}
                style={{ aspectRatio: slotAspectRatio }}
                className="relative [&_img]:object-contain [&_video]:object-contain"
              >
                {renderUploadSlot(index)}
                {!isFilled ? (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0"
                  >
                    <StatusPill
                      tone={isRequiredSlot ? 'required' : 'optional'}
                      className="absolute left-2 top-2"
                    >
                      {isRequiredSlot ? 'Required' : 'Optional'}
                    </StatusPill>
                    <div className="flex h-full flex-col items-center justify-center">
                      <span className="h-12" />
                      <span className="text-xs leading-4 text-white">
                        {isRequiredSlot ? 'Add' : 'Add more'}
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {filled > 0 ? (
        <div className="flex flex-col gap-1">
          {filled < MIN_PREVIEW_IMAGES ? (
            <Form.FieldFeedback className="justify-start tabular-nums">
              {MIN_PREVIEW_IMAGES - filled} more required
            </Form.FieldFeedback>
          ) : (
            <>
              <Form.FieldFeedback
                tone="success"
                className="justify-start tabular-nums"
              >
                <Check aria-hidden="true" />
                {filled} screenshots added
              </Form.FieldFeedback>
              {filled < MAX_PREVIEW_IMAGES ? (
                <Form.FieldFeedback
                  tone="muted"
                  className="justify-start tabular-nums"
                >
                  Add up to {MAX_PREVIEW_IMAGES - filled} more
                </Form.FieldFeedback>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
